import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { ShieldCheck, Eye, EyeOff } from "lucide-react";

export default function ChangePassword() {
    const nav = useNavigate();
    const { user, refresh } = useAuth();
    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [confirm, setConfirm] = useState("");
    const [show, setShow] = useState(false);
    const [saving, setSaving] = useState(false);

    const forced = !!user?.force_password_change;

    const submit = async (e) => {
        e.preventDefault();
        if (next.length < 8) {
            toast.error("New password must be at least 8 characters");
            return;
        }
        if (next !== confirm) {
            toast.error("New password and confirmation do not match");
            return;
        }
        setSaving(true);
        try {
            await api.post("/auth/change-password", {
                current_password: current,
                new_password: next,
            });
            toast.success("Password updated.");
            await refresh?.();
            nav("/dashboard", { replace: true });
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not change password");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="min-h-screen bg-bone flex items-center justify-center px-4 py-16" data-testid="change-password-page">
            <form onSubmit={submit} className="bg-white border border-ink/10 max-w-md w-full p-9">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-klein/10 text-klein flex items-center justify-center">
                        <ShieldCheck className="w-5 h-5" />
                    </div>
                    <div>
                        <div className="overline">{forced ? "Required" : "Account"}</div>
                        <h1 className="mt-1 font-display font-bold text-2xl text-ink">Change password</h1>
                    </div>
                </div>
                {forced && (
                    <div className="mt-5 bg-amber-50 border border-amber-200 px-4 py-3 text-xs text-amber-800" data-testid="change-password-forced-banner">
                        Your administrator requires you to set your own password before continuing.
                    </div>
                )}
                <div className="mt-6 space-y-4">
                    <Field
                        label="Current password"
                        value={current}
                        onChange={setCurrent}
                        type={show ? "text" : "password"}
                        autoComplete="current-password"
                        testid="cp-current"
                    />
                    <Field
                        label="New password"
                        value={next}
                        onChange={setNext}
                        type={show ? "text" : "password"}
                        autoComplete="new-password"
                        testid="cp-new"
                        hint="At least 8 characters. Use letters, numbers and symbols."
                    />
                    <Field
                        label="Confirm new password"
                        value={confirm}
                        onChange={setConfirm}
                        type={show ? "text" : "password"}
                        autoComplete="new-password"
                        testid="cp-confirm"
                    />
                    <label className="inline-flex items-center gap-2 cursor-pointer text-xs text-ink/60">
                        <input
                            type="checkbox"
                            checked={show}
                            onChange={(e) => setShow(e.target.checked)}
                            className="w-3.5 h-3.5 accent-klein"
                            data-testid="cp-show-toggle"
                        />
                        {show ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                        Show passwords
                    </label>
                </div>
                <button
                    type="submit"
                    disabled={saving}
                    className="mt-7 w-full bg-klein text-white py-3 text-sm font-medium hover:bg-klein/90 disabled:opacity-50"
                    data-testid="cp-submit"
                >
                    {saving ? "Updating…" : "Update password"}
                </button>
                {!forced && (
                    <button
                        type="button"
                        onClick={() => nav(-1)}
                        className="mt-3 w-full py-2 text-xs text-ink/50 hover:text-ink"
                        data-testid="cp-cancel"
                    >
                        Cancel
                    </button>
                )}
            </form>
        </div>
    );
}

function Field({ label, value, onChange, type, autoComplete, testid, hint }) {
    return (
        <div>
            <label className="overline block mb-2">{label}</label>
            <input
                type={type}
                autoComplete={autoComplete}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                required
                className="w-full border border-ink/15 py-2.5 px-3 text-sm focus:border-klein focus:outline-none"
                data-testid={testid}
            />
            {hint && <p className="text-[11px] text-ink/50 mt-1">{hint}</p>}
        </div>
    );
}
